const FTP_HINT = 'Per file più grandi usa il caricamento via FTP.'

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB']

const SIZE_PATTERN = /too large|greater than|exceed|kilobyte|megabyte|failed to upload|supera|troppo grand|dimension/i

const isPlainObject = value => typeof value === 'object' && value !== null && !Array.isArray(value)

const formatBytes = bytes => {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes < 0) {
    return ''
  }
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024
    unit += 1
  }
  const text = unit === 0 ? String(value) : value.toFixed(1).replace(/\.0$/, '').replace('.', ',')
  return `${text} ${UNITS[unit]}`
}

const oversizeMessage = human => {
  if (typeof human === 'string' && human !== '') {
    return `Il file supera il limite di caricamento (max ${human}). ${FTP_HINT}`
  }
  return `Il file supera il limite di caricamento del server. ${FTP_HINT}`
}

const getProgress = event => {
  if (typeof event !== 'object' || event === null) {
    return {
      phase: 'starting', percent: 0, barValue: 0, loaded: 0, total: 0, determinate: true,
    }
  }
  const loadedRaw = Number(event.loaded)
  const loaded = Number.isFinite(loadedRaw) && loadedRaw > 0 ? loadedRaw : 0
  const total = Number(event.total)
  if (Number.isFinite(total) && total > 0) {
    const percent = Math.min(100, Math.max(0, Math.floor((loaded / total) * 100)))
    return {
      phase: loaded >= total ? 'processing' : 'uploading',
      percent,
      barValue: percent,
      loaded,
      total,
      determinate: true,
    }
  }
  return {
    phase: 'uploading', percent: 0, barValue: 100, loaded, total: 0, determinate: false,
  }
}

const progressLabel = progress => {
  if (progress.phase === 'starting') {
    return 'Avvio del caricamento...'
  }
  if (progress.phase === 'processing') {
    return 'Invio completato: il server sta elaborando il file. Non chiudere la finestra.'
  }
  if (progress.determinate) {
    return `Caricamento in corso: ${progress.percent}% (${formatBytes(progress.loaded)} di ${formatBytes(progress.total)})`
  }
  return `Caricamento in corso: ${formatBytes(progress.loaded)} inviati`
}

const collectDetails = payload => {
  const details = []
  const keys = []
  if (!payload) {
    return { details, fileFieldFailed: false }
  }
  // Prima gli errori di campo (più specifici), poi il messaggio generico
  const groups = [payload.data, payload.errors]
  groups.forEach(group => {
    if (isPlainObject(group)) {
      Object.keys(group).forEach(key => {
        keys.push(key)
        if (Array.isArray(group[key])) {
          group[key].forEach(item => {
            if (typeof item === 'string') details.push(item)
          })
        }
      })
    }
  })
  if (typeof payload.message === 'string') {
    details.push(payload.message)
  }
  return { details, fileFieldFailed: keys.some(key => key.startsWith('file')) }
}

const TIMEOUT_MESSAGE = 'Il caricamento ha impiegato troppo tempo ed è stato interrotto (timeout). '
  + 'Il file potrebbe non essere stato caricato: controlla l\'elenco dei file prima di riprovare, '
  + 'oppure usa il caricamento via FTP per file molto grandi.'

const NETWORK_MESSAGE = 'Connessione interrotta durante il caricamento: il file non è stato caricato. '
  + 'Controlla la connessione e riprova. Se il file è molto grande potrebbe superare il limite del server: '
  + 'in tal caso usa il caricamento via FTP.'

const NOT_UPLOADED = ' Il file non è stato caricato.'

const REJECTED_STATUS_MESSAGES = {
  403: 'Permesso negato.',
  404: 'Cartella o file non trovato.',
  429: 'Troppe richieste. Riprova tra qualche istante.',
}

const classifyUploadFailure = (failure, context = {}) => {
  const ctx = isPlainObject(context) ? context : {}
  const error = isPlainObject(failure) ? failure : {}
  let response = null
  if (isPlainObject(error.response)) {
    response = error.response
  } else if (typeof error.status === 'number') {
    response = error
  }
  const status = response && typeof response.status === 'number' ? response.status : null
  const payload = response && isPlainObject(response.data) ? response.data : null
  const { details, fileFieldFailed } = collectDetails(payload)
  const sizeRelated = fileFieldFailed || details.some(detail => SIZE_PATTERN.test(detail))

  const oversize = () => {
    let message = oversizeMessage(ctx.limit && ctx.limit.human)
    if (ctx.fileCount > 1) {
      message += ' Se hai selezionato più file, prova a caricarne meno per volta.'
    }
    return { kind: 'oversize', status, message }
  }
  const rejected = () => {
    let message = 'Il server ha rifiutato il caricamento'
    if (details.length > 0) {
      message += `: ${details[0].replace(/[.\s]+$/, '')}`
    }
    return { kind: 'rejected', status, message: `${message}.${NOT_UPLOADED}` }
  }

  if (!response) {
    const timedOut = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT'
      || (typeof error.message === 'string' && /timeout/i.test(error.message))
    return timedOut
      ? { kind: 'timeout', status, message: TIMEOUT_MESSAGE }
      : { kind: 'network', status, message: NETWORK_MESSAGE }
  }
  if (status === 413) {
    return oversize()
  }
  if (status === 422) {
    return sizeRelated || details.length === 0 ? oversize() : rejected()
  }
  if (status >= 200 && status < 300 && payload && (payload.code === 0 || payload.code === '0')) {
    return sizeRelated ? oversize() : rejected()
  }
  if (status === 401) {
    return { kind: 'auth', status, message: 'Sessione scaduta: accedi di nuovo e ripeti il caricamento.' }
  }
  if (REJECTED_STATUS_MESSAGES[status]) {
    return { kind: 'rejected', status, message: `${REJECTED_STATUS_MESSAGES[status]}${NOT_UPLOADED}` }
  }
  if ([408, 502, 503, 504].includes(status)) {
    return {
      kind: 'gateway',
      status,
      message: `Il server non ha risposto in tempo (errore HTTP ${status}). `
        + 'Il caricamento potrebbe essere stato completato: controlla l\'elenco dei file prima di riprovare.',
    }
  }
  if (status >= 200 && status < 300) {
    return {
      kind: 'unexpected',
      status,
      message: 'Il server ha risposto in modo inatteso. '
        + 'Non è chiaro se il file sia stato caricato: controlla l\'elenco dei file prima di riprovare.',
    }
  }
  return {
    kind: 'server',
    status,
    message: `Caricamento non riuscito${status === null ? '' : ` (errore HTTP ${status})`}.${NOT_UPLOADED} Riprova più tardi.`,
  }
}

const parseUploadLimit = response => {
  const body = response && isPlainObject(response.data) ? response.data : null
  if (!body || !body.code || !isPlainObject(body.data)) {
    return null
  }
  const bytes = Number(body.data.max_upload_bytes)
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return null
  }
  const human = body.data.max_upload_human
  return {
    bytes,
    human: typeof human === 'string' && human !== '' ? human : formatBytes(bytes),
    limitedBy: typeof body.data.limited_by === 'string' ? body.data.limited_by : null,
  }
}

const notBlocked = () => ({
  blocked: false, kind: '', message: '', offenders: [],
})

const checkSelection = (files, limit) => {
  if (!Array.isArray(files) || files.length === 0 || !limit) {
    return notBlocked()
  }
  // validation_rule e upload_max_filesize valgono per singolo file
  const offenders = files
    .filter(file => file && file.size > limit.bytes)
    .map(file => `${file.name} (${formatBytes(file.size)})`)
  if (offenders.length > 0) {
    return {
      blocked: true, kind: 'oversize', message: oversizeMessage(limit.human), offenders,
    }
  }
  // post_max_size e upstream limitano l'intero corpo della richiesta (tutti i file insieme)
  if (limit.limitedBy === 'post_max_size' || limit.limitedBy === 'upstream') {
    const total = files.reduce((sum, file) => sum + ((file && file.size) || 0), 0)
    if (total > limit.bytes) {
      return {
        blocked: true,
        kind: 'total',
        message: `La dimensione totale dei file selezionati (${formatBytes(total)}) supera il limite di caricamento per richiesta `
          + `(max ${limit.human}). Carica meno file per volta oppure usa il caricamento via FTP.`,
        offenders: [],
      }
    }
  }
  return notBlocked()
}

export default {
  FTP_HINT,
  formatBytes,
  oversizeMessage,
  getProgress,
  progressLabel,
  classifyUploadFailure,
  parseUploadLimit,
  checkSelection,
}
